/**
 * First-visit dealer starter tour — one-screen modal.
 * Shown once per browser; can replay from Overview.
 */

import { loadPack } from "./api.js";

const STORAGE_KEY = "leadforge_tour_v1_done";
const TOUR_VERSION = 1;

export function tourSeen() {
  try {
    return localStorage.getItem(STORAGE_KEY) === String(TOUR_VERSION);
  } catch {
    return false;
  }
}

export function markTourSeen() {
  try {
    localStorage.setItem(STORAGE_KEY, String(TOUR_VERSION));
  } catch {
    /* private mode */
  }
}

export function resetTour() {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

function tourHtml(pack) {
  const n = pack?.lead_count ?? "—";
  const where =
    pack?.territory_id === "national"
      ? "nationwide"
      : pack?.territory_id || pack?.metro || "your territory";

  return `
    <div class="tour-backdrop" id="tour-backdrop" role="dialog" aria-modal="true" aria-labelledby="tour-title">
      <div class="tour-modal">
        <header class="tour-head">
          <div class="tour-badge">Quick start · 30 seconds</div>
          <h2 id="tour-title">Welcome to LeadForge</h2>
          <p class="tour-sub">
            Your contractor playbook for outdoor living:
            ranked homes, what to sell, and rough dollar potential
            ${n !== "—" ? ` · <strong>${n} leads</strong> ready ${where}` : ""}.
          </p>
        </header>

        <ol class="tour-steps">
          <li>
            <span class="tour-step-n">1</span>
            <div>
              <strong>Browse Leads</strong>
              <p>Each home shows priority (Hot / Solid), what to sell, and a package dollar range. Start with Hot.</p>
            </div>
          </li>
          <li>
            <span class="tour-step-n">2</span>
            <div>
              <strong>Review on Street View</strong>
              <p>Skip apartments and commercial before you drive. Mark “Looks good — measure” or “Skip”.</p>
            </div>
          </li>
          <li>
            <span class="tour-step-n">3</span>
            <div>
              <strong>Check Revenue</strong>
              <p><em>Likely revenue</em> = typical ticket × chance of close. Planning only — always site-measure before quoting.</p>
            </div>
          </li>
          <li>
            <span class="tour-step-n">4</span>
            <div>
              <strong>Grow with Get leads</strong>
              <p>Share the homeowner form for legal phone &amp; email. Never cold-call pack addresses without consent.</p>
            </div>
          </li>
        </ol>

        <div class="tour-callout">
          <strong>Remember:</strong> This tool is for licensed dealers.
          Addresses are for planning and measure trips — not spam lists.
        </div>

        <footer class="tour-foot">
          <button type="button" class="btn" id="tour-skip">Skip for now</button>
          <div class="tour-foot-right">
            <a class="btn" href="#/pack" id="tour-go-leads">See leads</a>
            <button type="button" class="btn primary" id="tour-done">Got it — let’s go</button>
          </div>
        </footer>
      </div>
    </div>`;
}

/**
 * Mount tour if not seen (or force=true). Returns true if shown.
 */
export function maybeShowTour(pack, { force = false } = {}) {
  if (!force && tourSeen()) return false;
  // Don't interrupt public homeowner form
  const hash = (location.hash || "#/").replace(/^#/, "") || "/";
  const path = hash.split("?")[0] || "/";
  if (!force && (path === "/capture" || path.startsWith("/capture"))) return false;

  // Remove existing
  document.getElementById("tour-root")?.remove();

  const root = document.createElement("div");
  root.id = "tour-root";
  root.innerHTML = tourHtml(pack || {});
  document.body.appendChild(root);
  document.body.classList.add("tour-open");

  const onKey = (e) => {
    if (e.key === "Escape") close(true);
  };

  function close(mark) {
    if (mark) markTourSeen();
    document.removeEventListener("keydown", onKey);
    root.remove();
    document.body.classList.remove("tour-open");
  }

  root.querySelector("#tour-done")?.addEventListener("click", () => close(true));
  root.querySelector("#tour-skip")?.addEventListener("click", () => close(true));
  root.querySelector("#tour-go-leads")?.addEventListener("click", () => close(true));
  root.querySelector("#tour-backdrop")?.addEventListener("click", (e) => {
    if (e.target.id === "tour-backdrop") close(true);
  });
  document.addEventListener("keydown", onKey);

  setTimeout(() => root.querySelector("#tour-done")?.focus(), 50);
  return true;
}

/** Button HTML for Overview “Replay tour” */
export function tourReplayButtonHtml() {
  return `<button type="button" class="btn" id="btn-replay-tour">Show starter tour again</button>`;
}

export function mountTourReplay() {
  const btn = document.getElementById("btn-replay-tour");
  if (!btn) return;
  btn.onclick = () => {
    resetTour();
    loadPack()
      .then((p) => maybeShowTour(p, { force: true }))
      .catch(() => maybeShowTour({}, { force: true }));
  };
}
